import React, { useState, useEffect } from "react";
import {
  NavLink,
  Outlet,
  useParams,
  useNavigate,
  Navigate,
  useLocation,
} from "react-router-dom";
import {
  LogOut,
  Settings,
  Shield,
  TrendingUp,
  CreditCard,
  Landmark,
  Users,
  Image,
  BarChart3,
  DollarSign,
  Trophy,
  Package,
  GraduationCap,
  MessageCircle,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { AffiliateHeaderContext } from "../../context/AffiliateHeaderContext";
import NoticeModal from "../ui/NoticeModal";
import { temporary_fix_disable_commission } from "../../utils/featureFlags";

const SidebarLink = ({ to, children, icon: Icon }) => {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `group flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-300 relative overflow-hidden ${
          isActive
            ? "bg-gradient-to-r from-[#B8460E]/20 to-[#8B3A0E]/20 text-[#B8460E] shadow-lg shadow-[#B8460E]/10 border border-[#B8460E]/30"
            : "text-gray-300 hover:text-white hover:bg-white/10 hover:shadow-md"
        }`
      }
    >
      {({ isActive }) => (
        <>
          {/* Animated background for active state */}
          {isActive && (
            <div className="absolute inset-0 bg-gradient-to-r from-[#B8460E]/10 to-transparent animate-pulse"></div>
          )}

          {/* Icon */}
          <Icon
            className={`h-5 w-5 relative z-10 transition-all duration-300 ${
              isActive
                ? "text-[#B8460E]"
                : "text-gray-400 group-hover:text-white"
            }`}
          />

          {/* Text */}
          <span className="relative z-10 transition-all duration-300">
            {children}
          </span>

          {/* Hover shine effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700 pointer-events-none"></div>
        </>
      )}
    </NavLink>
  );
};

const AffiliateLayout = () => {
  const { affiliateName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, isRoleLoading } = useAuth();
  const [headerContent, setHeaderContent] = useState(null);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const decodedAffiliateName = decodeURIComponent(affiliateName);

  // Note: Admin users can access affiliate pages to view team data
  // Only the homepage redirect logic applies - not blocking admin access here

  // Navigate admin back to main admin dashboard when logo is clicked
  const handleLogoClick = () => {
    if (isAdmin) {
      navigate("/dashboard");
    }
  };

  return (
    <AffiliateHeaderContext.Provider value={setHeaderContent}>
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#B8460E]/10 rounded-full blur-3xl animate-pulse"></div>
          <div
            className="absolute bottom-0 right-0 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "2s" }}
          ></div>
          <div
            className="absolute top-1/2 left-1/3 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "4s" }}
          ></div>
        </div>

        {/* Enhanced Sidebar - hidden on mobile */}
        <aside className="hidden md:block fixed inset-y-0 left-0 w-72 h-screen bg-black/40 backdrop-blur-xl border-r border-white/10 shadow-2xl z-40 overflow-hidden">
          {/* Sidebar background effects */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-white/5"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#B8460E]/5 via-transparent to-transparent"></div>

          {/* Sidebar content */}
          <div className="relative z-10 h-full flex flex-col">
            {/* Logo Section */}
            <div className="flex items-center justify-center h-20 border-b border-white/10 bg-white/5 backdrop-blur-sm">
              <button
                onClick={handleLogoClick}
                className={`relative w-28 h-20 overflow-hidden rounded-xl shadow-xl focus:outline-none ${
                  isAdmin ? "cursor-pointer" : "cursor-default"
                }`}
              >
                <img
                  src="/whiteolympus.png"
                  alt="Olympus"
                  className="w-full h-full object-contain"
                />
              </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-6 py-4 space-y-6">
              <div className="mb-4">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
                  Analytics
                </h3>
                {!temporary_fix_disable_commission && (
                  <SidebarLink to="." icon={TrendingUp}>
                    Performance
                  </SidebarLink>
                )}
                <SidebarLink to="leaderboard" icon={Trophy}>
                  Leaderboard
                </SidebarLink>
              </div>

              <div className="mb-4">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
                  Growth
                </h3>
                {/*
                  <SidebarLink to="education" icon={GraduationCap}>
                    Education
                  </SidebarLink>
                */}
                <SidebarLink to="creative-submission" icon={Image}>
                  Creative Submission
                </SidebarLink>
                <SidebarLink to="chat" icon={MessageCircle}>
                  OlympusChat
                </SidebarLink>
                <SidebarLink to="products" icon={Package}>
                  Products
                </SidebarLink>
              </div>

              <div className="mb-4">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
                  Earnings
                </h3>
                <SidebarLink to="payout" icon={DollarSign}>
                  Payout Center
                </SidebarLink>
                <SidebarLink to="referral-center" icon={Users}>
                  Referral Center
                </SidebarLink>
                <SidebarLink to="bank-accounts" icon={Landmark}>
                  Bank Accounts
                </SidebarLink>
              </div>

              <div className="mb-4">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
                  Account
                </h3>
                <SidebarLink to="settings" icon={Settings}>
                  Account Settings
                </SidebarLink>
              </div>
            </nav>

            {/* Bottom Actions */}
            <div className="px-4 py-4 border-t border-white/10 bg-gradient-to-r from-white/5 to-transparent space-y-2">
              {isAdmin && (
                <button
                  onClick={() => navigate("/dashboard")}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-blue-300 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 hover:border-blue-500/30 transition-all duration-300 group"
                >
                  <Shield className="h-4 w-4 group-hover:scale-110 transition-transform" />
                  <span>Admin Dashboard</span>
                </button>
              )}
              <button
                onClick={logout}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-300 rounded-lg hover:bg-red-500/20 hover:text-red-400 transition-all duration-300 group"
              >
                <LogOut className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </aside>

        {/* Mobile Top Bar */}
        <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-black/60 backdrop-blur-xl border-b border-white/10 z-40 flex items-center justify-between px-4">
          <button
            onClick={() => setIsMobileNavOpen(true)}
            className="text-gray-300 hover:text-white focus:outline-none"
            aria-label="Open navigation"
          >
            {/* Simple hamburger */}
            <span className="block w-6 h-0.5 bg-gray-300 mb-1"></span>
            <span className="block w-6 h-0.5 bg-gray-300 mb-1"></span>
            <span className="block w-6 h-0.5 bg-gray-300"></span>
          </button>
          <img src="/whiteolympus.png" alt="Olympus" className="h-8" />
          <button
            onClick={logout}
            className="text-sm text-red-300 hover:text-red-400"
            aria-label="Sign out"
          >
            Sign Out
          </button>
        </div>

        {/* Enhanced Header (desktop) */}
        <header className="fixed top-0 left-0 md:left-72 right-0 h-20 bg-black/40 backdrop-blur-xl border-b border-white/10 shadow-2xl z-30 hidden md:block">
          {/* Header background effects */}
          <div className="absolute inset-0 bg-gradient-to-r from-white/5 via-white/10 to-white/5"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#B8460E]/5 via-transparent to-[#B8460E]/5"></div>

          {/* Animated shine effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent translate-x-[-200%] animate-[shimmer_8s_ease-in-out_infinite] pointer-events-none"></div>

          {/* Header content */}
          <div className="relative z-10 h-full flex items-center justify-between px-8">
            {headerContent || (
              <>
                <div className="flex items-center space-x-4">
                  <div className="w-2 h-8 bg-gradient-to-b from-[#B8460E] to-[#8B3A0E] rounded-full"></div>
                  <div>
                    <h1 className="text-2xl font-bold text-white">
                      Performance Overview & Analytics
                    </h1>
                    <p className="text-gray-400 text-sm">
                      Track your ad performance and commission earnings
                    </p>
                  </div>
                </div>

                {/* User Info - Right Side */}
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#B8460E] to-[#8B3A0E] rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-sm">
                      {decodedAffiliateName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-semibold text-sm">
                      {decodedAffiliateName}
                    </p>
                    <p className="text-gray-400 text-xs">Affiliate Partner</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </header>

        {/* Main content area */}
        <main className="mt-14 md:mt-20 md:ml-72 min-h-[calc(100vh-3.5rem)] md:min-h-[calc(100vh-5rem)] relative z-10">
          <div className="max-w-7xl mx-auto px-8 py-8">
            <Outlet />
          </div>
        </main>

        {/* Notice Modal - hide on Bank Accounts page to avoid duplicate/bank-specific messaging */}
        {!(location && location.pathname.includes("/bank-accounts")) && (
          <NoticeModal />
        )}

        {/* Mobile Navigation Drawer */}
        {isMobileNavOpen && (
          <div className="md:hidden fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-black/60"
              onClick={() => setIsMobileNavOpen(false)}
            ></div>
            <div className="absolute top-0 left-0 bottom-0 w-72 bg-black/80 backdrop-blur-xl border-r border-white/10 shadow-2xl p-4 overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <img src="/whiteolympus.png" alt="Olympus" className="h-8" />
                <button
                  onClick={() => setIsMobileNavOpen(false)}
                  className="text-gray-300 hover:text-white"
                  aria-label="Close navigation"
                >
                  ✕
                </button>
              </div>
              {/* Reuse the same navigation structure */}
              <nav className="space-y-6">
                <div>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
                    Analytics
                  </h3>
                  {!temporary_fix_disable_commission && (
                    <SidebarLink to="." icon={TrendingUp}>
                      Performance
                    </SidebarLink>
                  )}
                  <SidebarLink to="leaderboard" icon={Trophy}>
                    Leaderboard
                  </SidebarLink>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
                    Growth
                  </h3>
                  <SidebarLink to="creative-submission" icon={Image}>
                    Creative Submission
                  </SidebarLink>
                  <SidebarLink to="chat" icon={MessageCircle}>
                    OlympusChat
                  </SidebarLink>
                  <SidebarLink to="products" icon={Package}>
                    Products
                  </SidebarLink>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
                    Earnings
                  </h3>
                  <SidebarLink to="payout" icon={DollarSign}>
                    Payout Center
                  </SidebarLink>
                  <SidebarLink to="referral-center" icon={Users}>
                    Referral Center
                  </SidebarLink>
                  <SidebarLink to="bank-accounts" icon={Landmark}>
                    Bank Accounts
                  </SidebarLink>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
                    Account
                  </h3>
                  <SidebarLink to="settings" icon={Settings}>
                    Account Settings
                  </SidebarLink>
                </div>

                <button
                  onClick={logout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-300 rounded-lg hover:bg-red-500/20 hover:text-red-400 transition-all duration-300"
                >
                  Sign Out
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>
    </AffiliateHeaderContext.Provider>
  );
};

export default AffiliateLayout;
